import type {
  SourceRelationOption,
  SourceRelationValue,
  CalculationPath,
  HelperLocation,
  BomType,
  SampleItem,
  PlacementItem,
  AttributeDetail,
  CustomColumn,
  SubLocation,
  SubLocationDef,
} from '../types';
import { cmControlBaseLocations, cmControlSublocations, customModuleLocations, PRODUCT_CORE_LOCATION_ID } from '../cm-control-config.constants';
import {
  customSectionPlacementArry,
  productCpAttributeList,
  productCpoutComeList,
  bomSelection,
  prouctBomOutComeList,
  prouctCustmSectionOutComeList,
  poProductoutComeList,
  filterCustomAttributesDropdownAndTextType,
} from '../../../helper';

export const buildOption = (label: string, name: string, _id?: string | number): SourceRelationOption => {
  const base: SourceRelationOption = { label, name };
  if (_id !== undefined) {
    base._id = _id;
  }
  return base;
};

const baseLocationOptions: SourceRelationOption[] = (cmControlBaseLocations as unknown as HelperLocation[]).map((loc) => {
  const base: SourceRelationOption = { label: loc.label, name: loc.name };
  if (loc._id !== undefined) base._id = loc._id;
  return base;
});

const customModuleLocationOptions: SourceRelationOption[] = (customModuleLocations as unknown as HelperLocation[]).map((loc) => {
  const base: SourceRelationOption = { label: loc.label, name: loc.name };
  if (loc._id !== undefined) base._id = loc._id;
  return base;
});

export const locationOptions: SourceRelationOption[] = [...baseLocationOptions, ...customModuleLocationOptions];

export const emptyRelation: SourceRelationValue = {
  location: null,
  locationType: null,
  locationPlacement: null,
  attribute: null,
  output: null,
  subLocation: null,
  tableColumn: null,
  customModule: null,
  customModuleTab: null,
  customModuleSection: null,
  customModuleSubSection: null,
  customModuleControl: null,
};

export const emptyPath: CalculationPath = {
  pathName: '',
  relation: emptyRelation,
  breadcrumb: '',
};

export const isDeepEqual = (a: unknown, b: unknown): boolean => {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
};

export const migratePath = (path: any): CalculationPath => {
  if (path.relation) {
    return path as CalculationPath;
  }
  const oldPath = path as {
    pathName?: string;
    location?: { label?: string; name?: string; _id?: number | string } | null;
    subLocation?: { label?: string; name?: string } | null;
    locationPlacement?: { label?: string; name?: string } | null;
    tableColumn?: { fieldName?: string } | null;
    breadcrumb?: string;
  };
  const relation: SourceRelationValue = {
    location:
      oldPath.location && oldPath.location.label && oldPath.location.name
        ? {
            label: oldPath.location.label,
            name: oldPath.location.name,
            ...(oldPath.location._id !== undefined && { _id: oldPath.location._id }),
          }
        : null,
    locationType: null,
    locationPlacement:
      oldPath.locationPlacement && oldPath.locationPlacement.label && oldPath.locationPlacement.name
        ? { label: oldPath.locationPlacement.label, name: oldPath.locationPlacement.name }
        : null,
    attribute: null,
    output: null,
    subLocation:
      oldPath.subLocation && oldPath.subLocation.label && oldPath.subLocation.name
        ? { label: oldPath.subLocation.label, name: oldPath.subLocation.name }
        : null,
    tableColumn: oldPath.tableColumn || null,
  };
  return {
    pathName: oldPath.pathName ?? '',
    relation,
    breadcrumb: oldPath.breadcrumb ?? '',
  };
};

export const buildBreadcrumb = (path: CalculationPath): string => {
  const relation = path.relation;
  if (!relation) return '';
  const parts: string[] = [];
  if (relation.location?.name === 'Custom_Modules') {
    const cmParts: string[] = ['Custom Modules'];
    if (relation.customModule?.label) cmParts.push(relation.customModule.label);
    if (relation.customModuleTab?.label) cmParts.push(relation.customModuleTab.label);
    if (relation.customModuleSection?.label) cmParts.push(relation.customModuleSection.label);
    if (relation.customModuleSubSection?.label) cmParts.push(relation.customModuleSubSection.label);
    if (relation.customModuleControl?.label) cmParts.push(relation.customModuleControl.label);
    return cmParts.join(' > ');
  }
  if (relation.location?.label) parts.push(relation.location.label);
  if (relation.subLocation?.label) parts.push(relation.subLocation.label);
  if (relation.locationPlacement?.label) parts.push(relation.locationPlacement.label);
  if (relation.attribute?.label) parts.push(relation.attribute.label);
  if (relation.output?.label) parts.push(relation.output.label);
  return parts.join(' > ');
};

export const setPlacementForLocation = (locationName?: string, locationTypeName?: string, placement_master?: PlacementItem[]): SourceRelationOption[] => {
  if (locationName === 'Bill_of_Materials') {
    if (!locationTypeName || locationTypeName === 'all_item') {
      return (placement_master || []).map((val: PlacementItem) => buildOption(val?.name, val?._id));
    }
    const filteredPlacementMasters = (placement_master || []).filter(
      (val: PlacementItem) => Array.isArray(val?.bomType) && (val?.bomType || []).includes(locationTypeName),
    );
    return filteredPlacementMasters.map((val: PlacementItem) => buildOption(val?.name, val?._id));
  }
  return customSectionPlacementArry.map((option) => buildOption(option.label, option.name));
};

export const getSubLocationWithType = (
  locId?: number | string,
  locName?: string,
  manualCustomColumns?: SourceRelationOption[],
  customAttributeDetail?: AttributeDetail[],
  sampleTrackingDates?: SampleItem[],
  subBrandAccess?: boolean,
): SubLocation[] => {
  if (locId === 12) {
    return (manualCustomColumns || []).map((col) => ({ label: col.label, name: col.name, type: 'string' as const }));
  }
  let subLocationArr = ((cmControlSublocations || []) as unknown as SubLocationDef[]).find((val) => val?.locationId === (locId as number | string));
  if (!subLocationArr || !subLocationArr.subLocations) {
    const findLocation = locationOptions.find((l) => l?.name === locName);
    subLocationArr = ((cmControlSublocations || []) as unknown as SubLocationDef[]).find((val) => val?.locationId === (findLocation?._id as number | string));
  }
  const manipulatedDynamicColumns: SubLocation[] = [];
  const mcColumn = filterCustomAttributesDropdownAndTextType((customAttributeDetail || []) as AttributeDetail[]);
  (mcColumn as AttributeDetail[])?.forEach((dyCol) => {
    if ((dyCol as AttributeDetail)?.isActive) manipulatedDynamicColumns.push({ label: dyCol?.keyName, name: dyCol?._id, type: 'string' });
  });
  const sampleTrackingArr: SubLocation[] = ((sampleTrackingDates || []) as SampleItem[]).map((dyCol) => ({
    label: dyCol?.name,
    name: dyCol?._id,
    type: 'date',
  }));
  let subLocations: SubLocation[] = [...(subLocationArr?.subLocations || [])];
  if (locName === 'Product_Information') subLocations = subLocations.concat(manipulatedDynamicColumns);
  if (locName === 'Samples') subLocations = subLocations.concat(sampleTrackingArr);
  if (subLocationArr?.locationId === PRODUCT_CORE_LOCATION_ID && subBrandAccess) {
    subLocations = subLocations.concat([{ label: 'Sub Brand', name: 'subBrand', type: 'string' }]);
  }
  return subLocations.filter((subLocationItem, index) => subLocations.findIndex((other) => other.name === subLocationItem.name) === index);
};

export const setSubLocationArr = (
  locId?: number | string,
  locName?: string,
  manualCustomColumns?: SourceRelationOption[],
  customAttributeDetail?: AttributeDetail[],
  sampleTrackingDates?: SampleItem[],
  subBrandAccess?: boolean,
): SourceRelationOption[] => {
  const subLocationsWithType = getSubLocationWithType(locId, locName, manualCustomColumns, customAttributeDetail, sampleTrackingDates, subBrandAccess);
  return subLocationsWithType.map((subLocationItem) => buildOption(subLocationItem.label, subLocationItem.name));
};

export const hideShowPlacement = (locationName?: string, locationTypeName?: string): boolean => {
  const type = locationTypeName;
  return (locationName === 'Bill_of_Materials' && !!type && type !== 'all_item') || (locationName === 'Samples' && !!type);
};

export const hideShowSubLocation = (relation: SourceRelationValue): boolean => {
  if (relation?.location?.name === 'Custom_Modules') return false;
  const locName = relation?.location?.name;
  const locId = relation?.location?._id;
  const out = relation?.output?.name;
  const attr = relation?.attribute?.name;
  const type = relation?.locationType?.name;
  return (
    ['Product_Core_Information', 'Product_Information', 'Supplier_Information', 'Specifications', 'Costing_Information'].includes(locName || '') ||
    (locId === 12 && !!relation?.output) ||
    (locName === 'Bill_of_Materials' && type !== 'all_item' && out === 'valueType') ||
    (locName === 'Samples' && !!attr) ||
    (locName === 'Purchase_Order' && attr === 'valueType') ||
    (locName === 'Completion_Indicator' && !!attr)
  );
};

export const hideShowAttribute = (relation: SourceRelationValue): boolean => {
  if (relation?.location?.name === 'Custom_Modules') return false;
  const locName = relation?.location?.name;
  const locId = relation?.location?._id;
  const placement = relation?.locationPlacement;
  const type = relation?.locationType?.name;
  return (
    [
      'Design_Details',
      'Specifications',
      'Testing',
      'Care/User_Document_Information',
      'Costing_Information',
      'Purchase_Order',
      'Product_Core_Information',
      'Product_Information',
      'Supplier_Information',
      'Completion_Indicator',
    ].includes(locName || '') ||
    locId === 12 ||
    (locName === 'Bill_of_Materials' && !!placement) ||
    (locName === 'Bill_of_Materials' && type === 'all_item') ||
    (locName === 'Samples' && !!placement?.name)
  );
};

export const hideShowOutput = (relation: SourceRelationValue): boolean => {
  if (relation?.location?.name === 'Custom_Modules') return false;
  const locName = relation?.location?.name;
  const locId = relation?.location?._id;
  const attr = relation?.attribute?.name;
  if ((locName === 'Bill_of_Materials' || locId === 12) && attr === 'valueType') return false;
  return (
    ((['Design_Details', 'Testing', 'Bill_of_Materials', 'Care/User_Document_Information', 'Custom_Section'].includes(locName || '') || locId === 12) &&
      !!relation?.attribute?.name) ||
    (locName === 'Purchase_Order' && attr === 'all_item')
  );
};

export const hideShowCustomModuleTab = (relation: SourceRelationValue): boolean => {
  return relation?.location?.name === 'Custom_Modules' && !!relation.customModule;
};

export const hideShowCustomModuleSection = (relation: SourceRelationValue): boolean => {
  return relation?.location?.name === 'Custom_Modules' && !!relation.customModule && !!relation.customModuleTab;
};

export const hideShowCustomModuleSubSection = (relation: SourceRelationValue): boolean => {
  return relation?.location?.name === 'Custom_Modules' && !!relation.customModule && !!relation.customModuleTab && !!relation.customModuleSection;
};

export const hideShowCustomModuleControl = (relation: SourceRelationValue): boolean => {
  return (
    relation?.location?.name === 'Custom_Modules' &&
    !!relation.customModule &&
    !!relation.customModuleTab &&
    !!relation.customModuleSection &&
    !!relation.customModuleSubSection
  );
};

export const setOptionsForAttribute = (relation: SourceRelationValue): SourceRelationOption[] => {
  const locName = relation?.location?.name;
  const locId = relation?.location?._id;
  if (
    [
      'Product_Core_Information',
      'Product_Information',
      'Supplier_Information',
      'Specifications',
      'Samples',
      'Costing_Information',
      'Completion_Indicator',
    ].includes(locName || '')
  ) {
    return [...productCpAttributeList.filter((attribute) => ['valueType'].includes(attribute?.name))].map((attributeItem) =>
      buildOption(attributeItem.label, attributeItem.name),
    );
  }
  if (locName === 'Purchase_Order') {
    return [...productCpAttributeList.filter((attribute) => ['all_item', 'valueType'].includes(attribute?.name))].map((attributeItem) =>
      buildOption(attributeItem.label, attributeItem.name),
    );
  }
  if (locName === 'Bill_of_Materials') {
    return [...productCpAttributeList.filter((attribute) => attribute?.name !== 'valueType')].map((attributeItem) =>
      buildOption(attributeItem.label, attributeItem.name),
    );
  }
  if (['Design_Details'].includes(locName || '')) {
    return [...productCpAttributeList.filter((attribute) => ['first_item', 'last_item'].includes(attribute?.name))].map((attributeItem) =>
      buildOption(attributeItem.label, attributeItem.name),
    );
  }
  if (['Testing', 'Care/User_Document_Information'].includes(locName || '')) {
    return [...productCpAttributeList.filter((attribute) => !['valueType'].includes(attribute?.name))].map((attributeItem) =>
      buildOption(attributeItem.label, attributeItem.name),
    );
  }
  if (locId === 12) {
    return [...productCpAttributeList.filter((attribute) => attribute?.name !== 'all_item')].map((attributeItem) =>
      buildOption(attributeItem.label, attributeItem.name),
    );
  }
  return productCpAttributeList.map((attributeItem) => buildOption(attributeItem.label, attributeItem.name));
};

export const testingOutputList = (relation: SourceRelationValue): SourceRelationOption[] => {
  const attr = relation?.attribute?.name;
  return productCpoutComeList
    .filter((outcomeItem) =>
      ['first_item', 'last_item'].includes(attr || '') ? ['date_added'].includes(outcomeItem?.name) : outcomeItem?.name === 'percentage_of_approval',
    )
    .map((outcomeItem) => buildOption(outcomeItem.label, outcomeItem.name));
};

export const setOptionsForOutput = (relation: SourceRelationValue): SourceRelationOption[] => {
  const locName = relation?.location?.name;
  const locId = relation?.location?._id;
  const attr = relation?.attribute?.name;
  if (['Testing', 'Care/User_Document_Information'].includes(locName || '')) {
    return testingOutputList(relation);
  }
  if (locName === 'Design_Details') {
    const extras: SourceRelationOption[] = [{ label: 'Status Date', name: 'status_date' }];
    return [
      ...productCpoutComeList
        .filter((outcomeItem) => ['date_added'].includes(outcomeItem?.name))
        .map((outcomeItem) => buildOption(outcomeItem.label, outcomeItem.name)),
      ...extras,
    ];
  }
  if (locId === 12) {
    return productCpoutComeList
      .filter((outcomeItem) => ['date_added'].includes(outcomeItem?.name))
      .map((outcomeItem) => buildOption(outcomeItem.label, outcomeItem.name));
  }
  if (locName === 'Purchase_Order') {
    return poProductoutComeList
      .filter((outcomeItem) => outcomeItem?.name === 'completion_percentage')
      .map((outcomeItem) => buildOption(outcomeItem.label, outcomeItem.name));
  }
  if (locName === 'Bill_of_Materials') {
    if (relation?.locationType?.name !== 'all_item' && ['first_item', 'last_item'].includes(attr || '')) {
      return productCpoutComeList
        .filter((outcomeItem) => ['date_added'].includes(outcomeItem?.name))
        .map((outcomeItem) => buildOption(outcomeItem.label, outcomeItem.name));
    }
    return (
      ['first_item', 'last_item'].includes(attr || '')
        ? productCpoutComeList.filter((outcomeItem) => ['date_added'].includes(outcomeItem?.name))
        : productCpoutComeList.filter((outcomeItem) => outcomeItem?.name === 'percentage_of_approval')
    ).map((outcomeItem) => buildOption(outcomeItem.label, outcomeItem.name));
  }
  return (prouctCustmSectionOutComeList as unknown as Array<{ label: string; name: string }>).map((outcomeItem) =>
    buildOption(outcomeItem.label, outcomeItem.name),
  );
};

export const setOptionsForBomOutput = (relation: SourceRelationValue): SourceRelationOption[] => {
  const locName = relation?.location?.name;
  const type = relation?.locationType?.name;
  const attr = relation?.attribute?.name;
  if (locName === 'Bill_of_Materials' && type !== 'all_item' && ['first_item', 'last_item'].includes(attr || '')) {
    return (prouctBomOutComeList as unknown as Array<{ label: string; name: string }>)
      .filter((ele) => ele.name !== 'percentage_of_approval')
      .map((outcomeItem) => buildOption(outcomeItem.label, outcomeItem.name));
  }
  if (locName === 'Bill_of_Materials' && type === 'all_item' && ['first_item', 'last_item'].includes(attr || '')) {
    return (prouctBomOutComeList as unknown as Array<{ label: string; name: string }>)
      .filter((ele) => ele.name === 'date_added')
      .map((outcomeItem) => buildOption(outcomeItem.label, outcomeItem.name));
  }
  if (locName === 'Bill_of_Materials' && attr === 'all_item') {
    return (prouctBomOutComeList as unknown as Array<{ label: string; name: string }>)
      .filter((ele) => ele.name === 'percentage_of_approval')
      .map((outcomeItem) => buildOption(outcomeItem.label, outcomeItem.name));
  }
  return (prouctCustmSectionOutComeList as unknown as Array<{ label: string; name: string }>).map((outcomeItem) =>
    buildOption(outcomeItem.label, outcomeItem.name),
  );
};

export const setAttrinuteToValue = (getLocation?: string, getLocationPlacment?: string): boolean => {
  return (
    ['Product_Core_Information', 'Product_Information', 'Supplier_Information', 'Specifications', 'Costing_Information', 'Completion_Indicator'].includes(
      getLocation || '',
    ) ||
    (getLocation === 'Samples' && ['earliest', 'mostRecent'].includes(getLocationPlacment || ''))
  );
};

export const isPathComplete = (path: CalculationPath): boolean => {
  const relation = path.relation;
  if (!path.pathName || !relation?.location) return false;
  if (relation.location?.name === 'Custom_Modules') {
    return !!(
      path.pathName &&
      relation.customModule &&
      relation.customModuleTab &&
      relation.customModuleSection &&
      relation.customModuleSubSection &&
      relation.customModuleControl
    );
  }
  const needsType = ['Bill_of_Materials', 'Samples'].includes(relation.location?.name || '');
  const needsPlacement = hideShowPlacement(relation.location?.name, relation.locationType?.name);
  const needsAttribute = hideShowAttribute(relation);
  const needsOutput = hideShowOutput(relation);
  const needsSubLocation = hideShowSubLocation(relation);

  if (needsType && !relation.locationType) return false;
  if (needsPlacement && !relation.locationPlacement) return false;
  if (needsAttribute && !relation.attribute) return false;
  if (needsOutput && !relation.output) return false;
  if (needsSubLocation && !relation.subLocation) return false;

  return true;
};

export const checkPathHasStringType = (
  path: CalculationPath,
  manualCustomColumns?: SourceRelationOption[],
  customAttributeDetail?: AttributeDetail[],
  sampleTrackingDates?: SampleItem[],
  subBrandAccess?: boolean,
): boolean => {
  if (path.relation?.location?.name === 'Custom_Modules') return false;
  if (!path.relation?.subLocation?.name || !path.relation?.location) {
    return false;
  }

  const subLocationsWithType = getSubLocationWithType(
    path.relation.location._id,
    path.relation.location.name,
    manualCustomColumns,
    customAttributeDetail,
    sampleTrackingDates,
    subBrandAccess,
  );
  const subLocation = subLocationsWithType.find((sl) => sl.name === path.relation?.subLocation?.name);
  return subLocation?.type === 'string';
};

export const buildDynamicBomSelection = (bomTypes?: BomType[]): SourceRelationOption[] => {
  const serverTypes: SourceRelationOption[] =
    ((bomTypes || []) as BomType[])
      .filter((t) => (Object.prototype.hasOwnProperty.call(t ?? {}, 'isActive') ? (t as BomType)?.isActive === true : true))
      .map((bt) => buildOption(bt.name, bt._id)) ?? [];
  const staticTypes: SourceRelationOption[] = ((bomSelection || []) as Array<{ label: string; name: string }>).map((selectionItem) =>
    buildOption(selectionItem.label, selectionItem.name),
  );
  const seen = new Set<string>();
  const merged: SourceRelationOption[] = [];
  [...serverTypes, ...staticTypes].forEach((option) => {
    const key = String(option.name);
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(option);
    }
  });
  const allIdx = merged.findIndex((o) => o.name === 'all_item');
  if (allIdx > 0) {
    const allItem = merged[allIdx];
    if (allItem) {
      merged.splice(allIdx, 1);
      merged.unshift(allItem);
    }
  }
  return merged;
};

export const buildManualCustomColumns = (customColumnList?: { columns?: CustomColumn[] }): SourceRelationOption[] => {
  if (!customColumnList) return [];
  return (
    ((customColumnList?.columns ?? []) as CustomColumn[])
      .filter((c) => c?.columnType === 'manual' && (c?.type === 'Number' || c?.type === 'Number_percentage'))
      .map((column) => buildOption(column?.title, column?._id)) || []
  );
};

export const buildSampleTypeFromStore = (mainSampleList?: SampleItem[], otherSamplesList?: SampleItem[]): SourceRelationOption[] => {
  const main = mainSampleList ?? [];
  const other = otherSamplesList ?? [];
  return [...main, ...other].map((element) => buildOption(element?.name, element?._id));
};

export const checkPathReturnsNumber = (
  path: CalculationPath,
  manualCustomColumns?: SourceRelationOption[],
  customAttributeDetail?: AttributeDetail[],
  sampleTrackingDates?: SampleItem[],
  subBrandAccess?: boolean,
): boolean => {
  const relation = path.relation;
  if (!relation?.location) return false;

  const locId = relation.location._id;
  const locName = relation.location.name;
  const outputName = relation.output?.name;
  const subLocationName = relation.subLocation?.name;

  if (outputName === 'percentage_of_approval' || outputName === 'completion_percentage') {
    return true;
  }

  if (locId === 12 && manualCustomColumns) {
    const column = manualCustomColumns.find((col) => col.name === subLocationName);
    if (column) {
      return true;
    }
  }

  if (subLocationName) {
    const subLocationsWithType = getSubLocationWithType(locId, locName, manualCustomColumns, customAttributeDetail, sampleTrackingDates, subBrandAccess);
    const subLocation = subLocationsWithType.find((sl) => sl.name === subLocationName);
    if (subLocation?.type === 'number') {
      return true;
    }
  }

  return false;
};
